import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card"
import {
  type CalculationResult,
  type CalculatorInput,
  formatNumber,
  formatPercent,
} from "@/shared/lib/staffing-shared"

import { WhatIfTable } from "./WhatIfTable"

type ResultsStatus = "idle" | "errors" | "calculating" | "complete"

type ResultsPanelProps = {
  result: CalculationResult | null
  input: CalculatorInput | null
  periodDescriptorKey: string
  status: ResultsStatus
}

type ProgressConfig = {
  current: number
  target?: number
  label: string
  className?: string
  targetClassName?: string
}

type SummaryMetric = {
  label: string
  value: string
  helper?: string
  progress?: ProgressConfig
}

type Assumption = {
  label: string
  value: string
}

const clampToUnitInterval = (value: number) => {
  if (!Number.isFinite(value)) {
    return 0
  }

  return Math.max(0, Math.min(1, value))
}

const createSummaryMetrics = (
  result: CalculationResult,
  input: CalculatorInput,
  t: TFunction,
): SummaryMetric[] => {
  const {
    metrics,
    recommendedAgentsAvailable,
    recommendedScheduledAgents,
    serviceLevelTarget,
    occupancyTarget,
  } = result

  const serviceLevelProgress = clampToUnitInterval(metrics.serviceLevel)
  const serviceLevelTargetProgress = clampToUnitInterval(serviceLevelTarget)
  const occupancyProgress = clampToUnitInterval(metrics.occupancy)
  const occupancyTargetProgress = clampToUnitInterval(occupancyTarget)
  const occupancyExceeded = occupancyProgress > occupancyTargetProgress
  const scheduledAgentsLabel = t("common.agents", {
    count: recommendedScheduledAgents,
    formatted: formatNumber(recommendedScheduledAgents),
  })
  const availableAgentsLabel = t("common.agents", {
    count: recommendedAgentsAvailable,
    formatted: formatNumber(recommendedAgentsAvailable),
  })

  return [
    {
      label: t("results.metrics.agentsRequired.label"),
      value: t("results.metrics.agentsRequired.value", {
        agents: scheduledAgentsLabel,
      }),
      helper: t("results.metrics.agentsRequired.helper", {
        available: availableAgentsLabel,
      }),
    },
    {
      label: t("results.metrics.serviceLevel.label"),
      value: formatPercent(metrics.serviceLevel, 1),
      helper: t("results.metrics.serviceLevel.helper", {
        target: formatPercent(serviceLevelTarget, 0),
      }),
      progress: {
        current: serviceLevelProgress,
        target: serviceLevelTargetProgress,
        label: t("results.metrics.serviceLevel.progressLabel"),
        className: "bg-[var(--color-brand-600)]",
      },
    },
    {
      label: t("results.metrics.occupancy.label"),
      value: formatPercent(metrics.occupancy, 1),
      helper: t("results.metrics.occupancy.helper", {
        cap: formatPercent(occupancyTarget, 0),
      }),
      progress: {
        current: occupancyProgress,
        target: occupancyTargetProgress,
        label: t("results.metrics.occupancy.progressLabel"),
        className: occupancyExceeded
          ? "bg-[var(--color-brand-800)]"
          : "bg-[var(--color-brand-600)]",
        targetClassName: occupancyExceeded
          ? "bg-[var(--color-brand-800)]"
          : "bg-[var(--color-text-secondary)]",
      },
    },
    {
      label: t("results.metrics.calls.label"),
      value: formatNumber(input.incomingContacts),
      helper: t("results.metrics.calls.helper"),
    },
  ]
}

const createAssumptions = (
  input: CalculatorInput,
  periodDescriptorKey: string,
  trafficErlangs: string,
  serviceLevelTarget: number,
  occupancyTarget: number,
  shrinkagePercent: string,
  t: TFunction,
): Assumption[] => [
  {
    label: t("results.assumptions.contacts", { period: t(periodDescriptorKey) }),
    value: formatNumber(input.incomingContacts),
  },
  {
    label: t("results.assumptions.traffic"),
    value: t("results.assumptions.trafficValue", { value: trafficErlangs }),
  },
  {
    label: t("results.assumptions.handleTime"),
    value: t("common.units.seconds", { value: formatNumber(input.averageHandleTimeSeconds) }),
  },
  {
    label: t("results.assumptions.serviceLevel"),
    value: t("results.assumptions.serviceLevelValue", {
      percent: formatPercent(serviceLevelTarget, 0),
      seconds: t("common.units.seconds", { value: formatNumber(input.targetAnswerTimeSeconds) }),
    }),
  },
  {
    label: t("results.assumptions.shrinkage"),
    value: t("common.values.percent", { value: shrinkagePercent }),
  },
  {
    label: t("results.assumptions.occupancy"),
    value: formatPercent(occupancyTarget, 0),
  },
]

const buildWarnings = (result: CalculationResult, t: TFunction): string[] => {
  const messages = new Set<string>()

  if (!result.metServiceLevelTarget) {
    messages.add(t("results.warnings.serviceLevel"))
  }

  if (result.exceededOccupancyCap) {
    messages.add(t("results.warnings.occupancy"))
  }

  result.calculationWarnings.forEach((code) => {
    const translationKey = `results.warnings.${code}`
    const translated = t(translationKey)
    if (translated === translationKey || !translated) {
      messages.add(t("results.warnings.generic", { code }))
    } else {
      messages.add(translated)
    }
  })

  result.validationErrors.forEach((message) => {
    if (message) {
      messages.add(message)
    }
  })

  return Array.from(messages)
}

const SummaryMetricCard = ({ metric }: { metric: SummaryMetric }) => {
  const shouldCenter = !metric.progress

  return (
    <div
      className={`stack-sm h-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]/90 p-5 ${shouldCenter ? "text-center" : ""}`}
    >
      <dt className="body-sm font-medium text-[var(--color-text-primary)]">{metric.label}</dt>
      <dd className="heading-sm text-[var(--color-text-primary)]">{metric.value}</dd>
      {metric.progress ? <MetricProgress progress={metric.progress} /> : null}
      {metric.helper ? (
        <span className="body-xs text-[var(--color-text-secondary)]">{metric.helper}</span>
      ) : null}
    </div>
  )
}

const MetricProgress = ({ progress }: { progress: ProgressConfig }) => {
  const currentPercent = Math.round(clampToUnitInterval(progress.current) * 100)
  const targetPercent =
    typeof progress.target === "number"
      ? Math.round(clampToUnitInterval(progress.target) * 100)
      : null

  const targetStyle =
    targetPercent !== null && targetPercent >= 100
      ? { right: 0 }
      : targetPercent !== null
        ? { left: `${targetPercent}%` }
        : undefined

  return (
    <>
      <div className="flex flex-col gap-[var(--space-xs)]">
        <div className="relative h-2 w-full rounded-full bg-[var(--color-border)]/70">
          <span
            aria-hidden
            className={`absolute left-0 top-0 h-full rounded-full transition-all duration-300 ease-out ${progress.className ?? "bg-[var(--color-brand-600)]"}`}
            style={{ width: `${currentPercent}%` }}
          />
          {targetPercent !== null ? (
            <span
              aria-hidden
              className={`pointer-events-none absolute top-1/2 h-3 w-[2px] -translate-y-1/2 rounded ${progress.targetClassName ?? "bg-[var(--color-text-secondary)]"} ${targetPercent >= 100 ? "right-0 translate-x-0" : "-translate-x-1/2"}`}
              style={targetStyle}
            />
          ) : null}
        </div>
        <span className="sr-only">
          {`${progress.label}: ${currentPercent}%${targetPercent !== null ? `; target ${targetPercent}%` : ""}`}
        </span>
      </div>
    </>
  )
}

const ResultsStateCard = ({ title, description }: { title: string; description: string }) => (
  <Card aria-live="polite">
    <CardContent className="stack-md items-center justify-center py-16 text-center">
      <div className="stack-xs max-w-lg">
        <h2 className="heading-sm text-[var(--color-text-primary)]">{title}</h2>
        <p className="body-sm text-[var(--color-text-secondary)]">{description}</p>
      </div>
    </CardContent>
  </Card>
)

const ResultsSkeleton = ({ title, description }: { title: string; description: string }) => (
  <Card aria-live="polite" className="overflow-visible">
    <CardHeader className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/60">
      <CardTitle className="heading-md text-[var(--color-text-primary)]">{title}</CardTitle>
      <CardDescription className="body-sm text-[var(--color-text-secondary)]">
        {description}
      </CardDescription>
    </CardHeader>
    <CardContent className="animate-pulse space-y-6 py-8">
      <div className="h-4 rounded bg-[var(--color-border)]/70" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            className="space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-muted)]/60 p-5"
            key={index}
          >
            <div className="h-3 w-1/2 rounded bg-[var(--color-border)]/80" />
            <div className="h-6 w-3/4 rounded bg-[var(--color-border)]/80" />
            <div className="h-2 w-full rounded bg-[var(--color-border)]/70" />
          </div>
        ))}
      </div>
      <div className="h-48 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)]/60" />
    </CardContent>
  </Card>
)

const ResultsWarnings = ({ messages, title }: { messages: string[]; title: string }) => (
  <div className="stack-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)]/80 p-4 text-left">
    <p className="body-sm font-medium text-[var(--color-text-primary)]">{title}</p>
    <ul className="list-disc space-y-2 pl-5">
      {messages.map((message) => (
        <li className="body-sm text-[var(--color-text-secondary)]" key={message}>
          {message}
        </li>
      ))}
    </ul>
  </div>
)

export function ResultsPanel({ result, input, periodDescriptorKey, status }: ResultsPanelProps) {
  const { t } = useTranslation()

  if (status === "idle") {
    return (
      <ResultsStateCard
        description={t("results.states.idleBody")}
        title={t("results.states.idleTitle")}
      />
    )
  }

  if (status === "errors") {
    return (
      <ResultsStateCard
        description={t("results.states.errorBody")}
        title={t("results.states.errorTitle")}
      />
    )
  }

  if (status === "calculating") {
    return (
      <ResultsSkeleton
        description={t("results.states.loadingBody")}
        title={t("results.states.loadingTitle")}
      />
    )
  }

  if (!result || !input) {
    return (
      <ResultsStateCard
        description={t("results.states.idleBody")}
        title={t("results.states.idleTitle")}
      />
    )
  }

  const {
    metrics,
    recommendedAgentsAvailable,
    recommendedScheduledAgents,
    serviceLevelTarget,
    occupancyTarget,
  } = result

  const shrinkagePercent = Number.isInteger(input.shrinkagePercent)
    ? formatNumber(input.shrinkagePercent)
    : formatNumber(input.shrinkagePercent, 1)
  const shrinkagePercentDisplay = t("common.values.percent", { value: shrinkagePercent })
  const periodDescriptor = t(periodDescriptorKey)
  const asaSeconds = Number.isFinite(metrics.asa) ? metrics.asa.toFixed(1) : "0.0"
  const trafficErlangs = Number.isFinite(result.trafficIntensity)
    ? result.trafficIntensity.toFixed(3)
    : "0.000"
  const summaryMetrics = createSummaryMetrics(result, input, t)
  const assumptions = createAssumptions(
    input,
    periodDescriptorKey,
    trafficErlangs,
    serviceLevelTarget,
    occupancyTarget,
    shrinkagePercent,
    t,
  )
  const assumptionsListLabel = t("results.assumptions.listAriaLabel")
  const warnings = buildWarnings(result, t)

  const scheduledAgentsText = t("common.agents", {
    count: recommendedScheduledAgents,
    formatted: formatNumber(recommendedScheduledAgents),
  })
  const availableAgentsText = t("common.agents", {
    count: recommendedAgentsAvailable,
    formatted: formatNumber(recommendedAgentsAvailable),
  })

  const summaryDescription = t("results.summaryDescription", {
    scheduled: scheduledAgentsText,
    shrinkage: shrinkagePercentDisplay,
    available: availableAgentsText,
  })

  const performanceDescription = t("results.performanceDescription", {
    serviceLevel: formatPercent(metrics.serviceLevel, 1),
    answerTime: t("common.units.seconds", {
      value: formatNumber(input.targetAnswerTimeSeconds),
    }),
    asaSeconds: t("common.units.seconds", { value: asaSeconds }),
  })

  const assumptionsText = t("results.assumptions.summary", {
    contacts: formatNumber(input.incomingContacts),
    period: periodDescriptor,
    traffic: trafficErlangs,
    handleTime: t("common.units.seconds", { value: formatNumber(input.averageHandleTimeSeconds) }),
    serviceLevel: formatPercent(serviceLevelTarget, 0),
    targetSeconds: t("common.units.seconds", {
      value: formatNumber(input.targetAnswerTimeSeconds),
    }),
    shrinkage: shrinkagePercentDisplay,
    occupancy: formatPercent(occupancyTarget, 0),
  })

  return (
    <div className="stack-xl animate-[fadeUp_0.45s_ease-out]" aria-live="polite">
      <Card aria-labelledby="results-heading" className="overflow-visible">
        <CardHeader className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/60">
          <div className="stack-xs">
            <CardTitle className="heading-md" id="results-heading" tabIndex={-1}>
              {t("results.heading")}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="stack-lg pt-6">
          <div className="stack-sm">
            <p className="body-base text-[var(--color-text-secondary)]">{summaryDescription}</p>
            <p className="body-base text-[var(--color-text-secondary)]">{performanceDescription}</p>
          </div>

          <dl className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
            {summaryMetrics.map((metric) => (
              <SummaryMetricCard key={metric.label} metric={metric} />
            ))}
          </dl>

          <div className="stack-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)]/70 p-5 text-left">
            <div className="stack-xs mb-3">
              <h3 className="heading-sm text-[var(--color-text-primary)]">
                {t("results.assumptions.title")}
              </h3>
              <p className="body-xs text-[var(--color-text-secondary)]">
                {t("results.assumptions.description")}
              </p>
            </div>
            <dl
              className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3"
              aria-label={assumptionsListLabel}
            >
              {assumptions.map((item) => (
                <div className="flex items-baseline justify-between gap-3" key={item.label}>
                  <dt className="body-xs font-medium text-[var(--color-text-secondary)]">
                    {item.label}
                  </dt>
                  <dd className="body-sm text-right text-[var(--color-text-primary)]">
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
            <span className="sr-only">{assumptionsText}</span>
          </div>

          {warnings.length > 0 ? (
            <ResultsWarnings messages={warnings} title={t("results.warningsTitle")} />
          ) : (
            <div className="stack-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)]/80 p-4 text-left">
              <p className="body-sm font-medium text-[var(--color-text-primary)]">
                {t("results.callout.title")}
              </p>
              <p className="body-sm text-[var(--color-text-secondary)]">
                {t("results.callout.body", { occupancyCap: formatPercent(occupancyTarget, 0) })}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <WhatIfTable
        occupancyTarget={occupancyTarget}
        recommendedScheduledAgents={recommendedScheduledAgents}
        scenarios={result.whatIfScenarios}
        serviceLevelTarget={serviceLevelTarget}
      />
    </div>
  )
}
